import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { Link, type LinkProps } from 'react-router-dom'
import styles from './Button.module.css'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'

interface CommonProps {
  variant?: ButtonVariant
  children: ReactNode
}

type NativeButtonProps = CommonProps &
  ButtonHTMLAttributes<HTMLButtonElement> & { to?: undefined }

type LinkButtonProps = CommonProps & Omit<LinkProps, 'className'> & { to: LinkProps['to'] }

export type ButtonProps = NativeButtonProps | LinkButtonProps

/**
 * The single button primitive. Renders a router `<Link>` when given `to`,
 * otherwise a native `<button>`. Variants keep one dominant action per view
 * visually distinct from secondary and destructive actions.
 */
export function Button(props: ButtonProps) {
  const { variant = 'secondary', children } = props
  const className = `${styles.button} ${styles[variant]}`

  if (props.to !== undefined) {
    const { variant: _variant, children: _children, ...linkProps } = props
    return (
      <Link {...linkProps} className={className}>
        {children}
      </Link>
    )
  }

  const { variant: _variant, children: _children, to: _to, ...buttonProps } = props
  return (
    <button {...buttonProps} className={className}>
      {children}
    </button>
  )
}
